import {
  createBroadcastComposeStore,
  type BroadcastComposeState,
  type BroadcastComposeStoreApi,
} from "./broadcastComposeStoreFactory";

/**
 * Module-level registry of compose stores that must survive route unmounts —
 * e.g. the new-email compose page's round trip through the tile editor
 * (FOG-1335). In-memory only: a page refresh intentionally drops drafts.
 *
 * Grows by one small store per site the user opens a draft on within an SPA
 * session; entries are removed on successful create or an intentional fresh
 * "New Broadcast" entry.
 */
const registry = new Map<string, BroadcastComposeStoreApi>();

/** Draft key for the new-email compose flow (the only registered flow today). */
export function newEmailComposeDraftKey(siteId: string): string {
  return `new-email:${siteId}`;
}

/**
 * Returns the existing store for `key`, or creates one seeded with
 * `initialState`. On a registry hit `initialState` is ignored — a hit means
 * an in-progress draft exists and defaults must not clobber it.
 */
export function getOrCreateComposeStore(
  key: string,
  initialState?: Partial<BroadcastComposeState>,
): BroadcastComposeStoreApi {
  let store = registry.get(key);
  if (!store) {
    store = createBroadcastComposeStore(initialState);
    registry.set(key, store);
  }
  return store;
}

export function clearComposeStore(key: string): void {
  registry.delete(key);
}

/** Test-only: inspect without creating. */
export function peekComposeStore(
  key: string,
): BroadcastComposeStoreApi | undefined {
  return registry.get(key);
}

/** Test-only: teardown between tests (module state leaks across tests otherwise). */
export function clearAllComposeStores(): void {
  registry.clear();
}
